'use client';

import React, { useState, useEffect } from 'react';

interface TabProps {
  label: string;
  children: React.ReactNode;
  id?: string;
  'aria-labelledby'?: string;
  'aria-hidden'?: boolean;
}

export const Tab: React.FC<TabProps> = ({ children, id, 'aria-labelledby': ariaLabelledBy, 'aria-hidden': ariaHidden }) => {
  return (
    <div 
      id={id} 
      className="tab-panel" 
      role="tabpanel"
      aria-labelledby={ariaLabelledBy}
      aria-hidden={ariaHidden}
    >
      {children}
    </div>
  );
};

interface TabsProps {
  children: React.ReactElement<TabProps>[];
  defaultTab?: number;
  className?: string;
  schemaType?: 'HowTo' | 'none';
}

export const Tabs: React.FC<TabsProps> = ({ 
  children, 
  defaultTab = 0,
  className = '',
  schemaType = 'none'
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Extract tab labels from children
  const tabs = React.Children.toArray(children) as React.ReactElement<TabProps>[];

  // Check scroll position to show/hide fade indicators
  const checkScroll = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  useEffect(() => {
    // Mark as enhanced when JS loads
    setIsEnhanced(true);
    
    // Check initial scroll state
    checkScroll();
    
    // Add scroll listener
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Also check on resize
      window.addEventListener('resize', checkScroll);
    }
    
    // Inject CSS for no-jump behavior
    if (!document.getElementById('tabs-base-styles')) {
      const style = document.createElement('style');
      style.id = 'tabs-base-styles';
      style.textContent = `
        /* Default state - first tab visible, others hidden */
        .tabs-container .tab-panel {
          display: none;
        }
        
        .tabs-container .tab-panel:first-child {
          display: block;
        }
        
        /* Enhanced state - controlled by JS */
        .tabs-container[data-enhanced="true"] .tab-panel {
          display: none;
        }
        
        .tabs-container[data-enhanced="true"] .tab-panel[aria-hidden="false"] {
          display: block;
        }
        
        /* Ensure all content remains in DOM for SEO */
        .tabs-container:not([data-enhanced="true"]) .tab-panel:not(:first-child) {
          position: absolute;
          left: -9999px;
          width: 1px;
          height: 1px;
          overflow: hidden;
        }
        
        /* Hide scrollbar for Chrome, Safari and Opera */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Hide scrollbar for IE, Edge and Firefox */
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        /* Edge fade indicators */
        .tabs-fade-container::before,
        .tabs-fade-container::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 1px; /* Stop before border */
          width: 24px;
          pointer-events: none;
          z-index: 10;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        .tabs-fade-container::before {
          left: 0;
          background: linear-gradient(to right, white, transparent);
        }
        
        .tabs-fade-container::after {
          right: 0;
          background: linear-gradient(to left, white, transparent);
        }
        
        /* Show fades when scrollable */
        .tabs-fade-container.can-scroll-left::before {
          opacity: 1;
        }
        
        .tabs-fade-container.can-scroll-right::after {
          opacity: 1;
        }
      `;
      document.head.appendChild(style);
    }
    
    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
      window.removeEventListener('resize', checkScroll);
    };
  }, [checkScroll]);

  // Generate HowTo schema if needed
  const howToSchema = schemaType === 'HowTo' && (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "name": "Process Steps",
          "step": tabs.map((tab, index) => ({
            "@type": "HowToStep",
            "name": tab.props.label,
            "text": typeof tab.props.children === 'string' 
              ? tab.props.children 
              : '',
            "url": `#tab-${index}`
          }))
        })
      }}
    />
  );

  return (
    <>
      {howToSchema}
      <div className={`my-8 mdx-component-base ${className}`} data-enhanced={isEnhanced}>
        {/* Tab Navigation */}
        <div 
          ref={scrollContainerRef}
          className={`relative border-b border-gray-200 overflow-x-auto scrollbar-hide scroll-smooth tabs-fade-container ${canScrollLeft ? 'can-scroll-left' : ''} ${canScrollRight ? 'can-scroll-right' : ''}`}
        >
          <nav className="flex -mb-px" role="tablist">
            {tabs.map((tab, index) => {
              const tabId = `tab-${index}`;
              const panelId = `panel-${index}`;
              const isActive = activeTab === index;
              
              return (
                <a
                  key={index}
                  href={`#${tabId}`}
                  onClick={(e) => {
                    if (isEnhanced) {
                      e.preventDefault();
                      setActiveTab(index);
                    }
                  }}
                  className={`
                    py-2 lg:py-3 px-4 lg:px-6 component-caption font-medium transition-colors no-underline whitespace-nowrap
                    ${isActive
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }
                  `}
                  aria-selected={isActive}
                  aria-controls={panelId}
                  role="tab"
                  id={tabId}
                >
                  {tab.props.label}
                </a>
              );
            })}
          </nav>
        </div>

        {/* Tab Content - All panels always in DOM */}
        <div className="tabs-container pt-4 lg:pt-6" data-enhanced={isEnhanced}>
          <div className="prose prose-sm lg:prose-lg max-w-none" style={{ textWrap: 'pretty' }}>
            {tabs.map((tab, index) => {
              const panelId = `panel-${index}`;
              const tabId = `tab-${index}`;
              const isActive = activeTab === index;
              
              return React.cloneElement(tab, {
                key: index,
                id: panelId,
                'aria-labelledby': tabId,
                'aria-hidden': !isActive
              });
            })}
          </div>
        </div>
      </div>
    </>
  );
};